import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  ReactNode,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Clear from '@mui/icons-material/Clear';
import type { SelectChangeEvent } from '@mui/material/Select';

const ALL_GROUPS = '__all__';

type OptionTagVariant = 'green' | 'blue' | 'amber' | 'rose' | 'violet';

type OptionTag = {
  label: string;
  variant?: OptionTagVariant;
};

export interface SharedGroupedSelectProps<T> {
  options: readonly T[];
  value: T | null;
  onChange: (event: SyntheticEvent | Event, value: T | null) => void;
  groupBy: (option: T) => string;
  getOptionLabel: (option: T) => string;
  getOptionDescription?: (option: T) => string;
  getOptionTags?: (option: T) => OptionTag[];
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  renderOption?: (option: T, selected: boolean) => ReactNode;
  renderValue?: (value: T | null) => ReactNode;
  placeholder?: string;
  error?: boolean;
  helperText?: ReactNode;
}

export default function SharedGroupedSelect<T>({
  options,
  value,
  onChange,
  groupBy,
  getOptionLabel,
  getOptionDescription,
  getOptionTags,
  isOptionEqualToValue = (a, b) => a === b,
  renderOption,
  renderValue,
  placeholder = 'Select a trigger event',
  error,
  helperText,
}: SharedGroupedSelectProps<T>) {
  const theme = useTheme();
  const anchorRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const variantStyles: Record<
    OptionTagVariant,
    {
      backgroundColor: string;
      textColor: string;
    }
  > = {
    green: {
      backgroundColor: theme.palette.semantic.chip.success,
      textColor: theme.palette.semantic.icon.success,
    },
    blue: {
      backgroundColor: theme.palette.semantic.chip.info,
      textColor: theme.palette.semantic.icon.blue,
    },
    amber: {
      backgroundColor: theme.palette.semantic.chip.warning,
      textColor: theme.palette.semantic.icon.warning,
    },
    rose: {
      backgroundColor: theme.palette.semantic.chip.error,
      textColor: theme.palette.semantic.icon.error,
    },
    violet: {
      backgroundColor: theme.palette.semantic.chip.violet,
      textColor: theme.palette.semantic.icon.violet,
    },
  };

  const groups = useMemo(
    () => [...new Set(options.map(groupBy))],
    [options, groupBy]
  );

  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [appliedGroups, setAppliedGroups] = useState<string[]>([]);
  const [pendingGroups, setPendingGroups] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && groups.length > 0) {
      setAppliedGroups(groups);
      setPendingGroups(groups);
      setInitialized(true);
    }
  }, [groups, initialized]);

  const filteredOptions = useMemo(() => {
    if (appliedGroups.length === 0 || appliedGroups.length === groups.length) {
      return options;
    }

    return options.filter((option) => appliedGroups.includes(groupBy(option)));
  }, [options, appliedGroups, groupBy, groups.length]);

  const groupedOptions = useMemo(() => {
    return filteredOptions.reduce(
      (acc, option) => {
        const group = groupBy(option);
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(option);
        return acc;
      },
      {} as Record<string, T[]>
    );
  }, [filteredOptions, groupBy]);

  // Handle clicking outside to close main popper
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (anchorRef.current?.contains(target)) return;
      if (popperRef.current?.contains(target)) return;
      if (
        target.closest('[role="presentation"]') ||
        target.closest('[role="listbox"]')
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Handle toggling checkboxes inside the filter
  const handleFilterChange = (
    event: SelectChangeEvent<typeof pendingGroups>
  ) => {
    const value = event.target.value as string[];

    if (value.includes(ALL_GROUPS)) {
      if (pendingGroups.length === groups.length) {
        setPendingGroups([]); // Deselect all
      } else {
        setPendingGroups(groups); // Select all
      }
    } else {
      setPendingGroups(value.filter((v) => v !== ALL_GROUPS));
    }
  };

  const handleApplyFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAppliedGroups(pendingGroups);
    setFilterMenuOpen(false);
  };

  const handleResetFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingGroups(groups);
  };

  const renderSelectFilterValue = () => {
    if (appliedGroups.length === groups.length || appliedGroups.length === 0) {
      return `All Categories (${options.length})`;
    }
    if (appliedGroups.length === 1) {
      const count = options.filter(
        (o) => groupBy(o) === appliedGroups[0]
      ).length;
      return `${appliedGroups[0]} (${count})`;
    } else {
      const count = options.filter((o) =>
        appliedGroups.includes(groupBy(o))
      ).length;

      return `Selected Categories (${count})`;
    }
  };

  return (
    <FormControl fullWidth error={error}>
      <Paper
        ref={anchorRef}
        variant="outlined"
        tabIndex={0}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="shared-grouped-select-listbox"
        sx={{
          px: 1.5,
          py: 0.8,
          cursor: 'pointer',
          borderColor: error
            ? 'error.main'
            : theme.palette.mode === 'light'
            ? theme.palette.colors.default
            : alpha(theme.palette.colors.white, 0.23),
          transition: (theme) =>
            theme.transitions.create('border-color', {
              duration: theme.transitions.duration.shortest,
            }),
          '&:hover': {
            borderColor: error ? 'error.main' : 'text.primary',
          },
          '&:focus-visible': {
            borderColor: error
              ? 'error.main'
              : theme.palette.semantic.outline.primary,
          },
        }}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box flex={1}>
            {!value ? (
              <Typography color={error ? 'error.main' : 'text.secondary'}>
                {placeholder ?? ''}
              </Typography>
            ) : renderValue ? (
              renderValue(value)
            ) : (
              <Typography color={error ? 'error.main' : 'text.primary'}>
                {getOptionLabel(value)}
              </Typography>
            )}
          </Box>
          {value && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onChange(e, null);
              }}
            >
              <Clear fontSize="small" />
            </IconButton>
          )}
          <KeyboardArrowDownIcon color="action" />
        </Stack>
      </Paper>
      <FormHelperText error={error}>{helperText}</FormHelperText>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{
          zIndex: 1300,
          width: '500px',
          pt: 1,
        }}
      >
        <Paper
          ref={popperRef}
          elevation={4}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          {/* Top Filter Selection */}
          <Box p={1.5}>
            <FormControl size="small" sx={{ width: '300px' }}>
              <Select
                multiple
                open={filterMenuOpen}
                onOpen={() => {
                  setPendingGroups(appliedGroups);
                  setFilterMenuOpen(true);
                }}
                onClose={() => setFilterMenuOpen(false)}
                value={pendingGroups}
                onChange={handleFilterChange}
                displayEmpty
                renderValue={renderSelectFilterValue}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      mt: 0.5,
                      '& .MuiMenuItem-root': {
                        height: 35,
                        py: 0.25,
                        '& .Mui-selected': {
                          backgroundColor: 'transparent',
                        },
                      },
                      '& .MuiListItemText-root': {
                        my: 0,
                      },
                    },
                  },
                }}
              >
                {/* Select All Checkbox */}
                <MenuItem
                  value={ALL_GROUPS}
                  sx={{
                    '& .Mui-selected': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <Checkbox
                    checked={pendingGroups.length === groups.length}
                    indeterminate={
                      pendingGroups.length > 0 &&
                      pendingGroups.length < groups.length
                    }
                  />
                  <ListItemText primary="All Categories" />
                </MenuItem>

                <Divider sx={{ mx: 2, my: 0.5 }} />

                {/* Individual Group Checkboxes */}
                {groups.map((group) => {
                  const count = options.filter(
                    (o) => groupBy(o) === group
                  ).length;
                  return (
                    <MenuItem
                      key={group}
                      value={group}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <Checkbox checked={pendingGroups.includes(group)} />
                      <ListItemText primary={`${group} (${count})`} />
                    </MenuItem>
                  );
                })}

                {/* Action Buttons Sticky Footer */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    p: 1.5,
                    pb: 1,
                    position: 'sticky',
                    bottom: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleResetFilters}
                    sx={{
                      color: theme.palette.semantic.button.secondary.text,
                      borderColor:
                        theme.palette.semantic.button.secondary.border,
                      textTransform: 'none',
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleApplyFilters}
                    sx={{
                      bgcolor: theme.palette.semantic.button.primary.background,
                      color: theme.palette.semantic.button.primary.text,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: theme.palette.semantic.button.primary.hover,
                      },
                    }}
                  >
                    Apply Filters
                  </Button>
                </Box>
              </Select>
            </FormControl>
          </Box>

          {/* Grouped Options List */}
          <List sx={{ maxHeight: 450, overflow: 'auto', p: 0 }}>
            {Object.entries(groupedOptions).map(([group, groupOptions]) => (
              <Box key={group}>
                {/* Group Header */}
                <Typography
                  sx={{
                    px: 2,
                    py: 1,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: 'text.primary',
                    bgcolor: theme.palette.semantic.surface.hover,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {group}
                </Typography>

                {/* Group Items */}
                {groupOptions.map((option) => {
                  const selected =
                    value != null && isOptionEqualToValue(option, value);

                  return (
                    <ListItemButton
                      key={getOptionLabel(option)}
                      selected={selected}
                      onClick={(event) => {
                        onChange(event, option);
                        setOpen(false);
                      }}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        px: 2,
                        py: 1.5,
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      {renderOption ? (
                        renderOption(option, selected)
                      ) : (
                        <Stack direction="column">
                          <Stack direction="row" gap={1}>
                            <Typography>{getOptionLabel(option)}</Typography>
                            {getOptionTags?.(option)?.map((tag) => {
                              const style =
                                variantStyles[tag.variant ?? 'blue'];

                              return (
                                <Chip
                                  key={tag.label}
                                  label={tag.label}
                                  size="small"
                                  sx={{
                                    bgcolor: style.backgroundColor,
                                    color: style.textColor,
                                    px: 0.5,
                                  }}
                                />
                              );
                            })}
                          </Stack>
                          {getOptionDescription && (
                            <Typography variant="caption">
                              {getOptionDescription(option)}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </ListItemButton>
                  );
                })}
              </Box>
            ))}
          </List>
        </Paper>
      </Popper>
    </FormControl>
  );
}
